<!-- Localized -->
<template>
    <modal name="profile-task-modal" ref="profile-task-modal" width="50%" height="55%" class="rounded-full" v-bind="$attrs" v-on="$listeners">
        <div class="flex flex-col justify-between min-h-full h-fullbg-card-bg h-full">
            <div class="modal-header w-full min-h-12 flex items-center justify-between px-6 bg-primary">
                <h3 class="font-semibold text-white-text flex items-center justify-center gap-2">
                    <TaskIcon />
                    {{ $t('components.tenant.sc_journey.task') }}
                </h3>
                <span class="__icon flex flex-col translate-x-full relative" role="button" @click="closeModal">
                    <CloseIcon />
                </span>
            </div>

            <div class="container px-6 flex-grow w-full max-w-full overflow-y-auto scroll-bar bg-card-bg">
                <div class="call-form-wrapper w-full h-full">
                    <FormulateForm class="call-form-container flex flex-col py-4 gap-4 w-full h-full">
                        <div class="flex flex-wrap gap-4 items-end">
                            <Field
                                type="date"
                                :disabled-date="DisableDate"
                                :title="$t('components.tenant.sc_journey.due_date')"
                                v-model="modalData.dueDate"
                                class="flex-1"
                                @input="handleDateChange"
                            >
                                <template #icon>
                                    <DateIcon />
                                </template>
                            </Field>
                            <Field
                                type="time"
                                :show-minute="false"
                                :show-second="false"
                                format="HH"
                                :disabled-time="disabledHours"
                                class="flex-1"
                                v-model="modalData.time"
                            >
                                <template #icon>
                                    <TimeIcon />
                                </template>
                            </Field>
                        </div>

                        <div class="mt-2">
                            <p class="font-semibold">{{ $t('components.tenant.sc_journey.assignee') }}</p>

                            <div class="flex flex-wrap gap-4 mt-2">
                                <FormulateInput
                                    v-if="!isClient"
                                    v-model="modalData.tenantUsers.model"
                                    :label="$t('components.sc_journey.note_users', { value: tenantName })"
                                    :placeholder="$t('components.sc_journey.select_users')"
                                    :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                                    :options="tenantUsersOptions"
                                    v-bind="multiselectConfig"
                                />

                                <FormulateInput
                                    v-model="modalData.clientUsers.model"
                                    :label="$t('components.sc_journey.note_users', { value: clientName }) "
                                    :placeholder="$t('components.sc_journey.select_users')"
                                    :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                                    :options="clientUsersOptions"
                                    v-bind="multiselectConfig"
                                />
                            </div>
                        </div>

                        <div class="mt-2">
                            <div class="flex items-center gap-2">
                                <UIToggle
                                    :selected="modalData.riskMitigation.mode"
                                    class="cursor-pointer"
                                    :disabled="isLoading"
                                    @select="toggleRiskMitigationMode"
                                />
                                <p>{{ $t('components.risk_mitigation.risk_control') }}</p>
                            </div>

                            <div class="flex items-center gap-4 mt-4">
                                <div class="flex-1">
                                    <p class="mb-2 text-sm">
                                        {{ $t('components.risk_mitigation.category') }}
                                        <span class="text-red-500">{{ $t('general.asterisk') }}</span>
                                    </p>
                                    <Dropdown
                                        :placeholder="$t('components.risk_mitigation.category')"
                                        :options="modalData.riskMitigation.category.options"
                                        :value="modalData.riskMitigation.category.model"
                                        v-bind="singleSelectConfig"
                                        :disabled="isSingleSelectDisabled"
                                        @input="(value) => setRiskMitigationField(value, 'category')"
                                    />
                                </div>

                                <div class="flex-1">
                                    <p class="mb-2 text-sm">
                                        {{ $t('components.risk_mitigation.subcategory') }}
                                        <span class="text-red-500">{{ $t('general.asterisk') }}</span>
                                    </p>
                                    <Dropdown
                                        :placeholder="$t('components.risk_mitigation.subcategory')"
                                        :options="modalData.riskMitigation.subCategory.options"
                                        :value="modalData.riskMitigation.subCategory.model"
                                        v-bind="singleSelectConfig"
                                        :disabled="isSubCategoryDisabled"
                                        @input="(value) => setRiskMitigationField(value, 'subCategory')"
                                    />
                                </div>

                                <div class="flex-1">
                                    <p class="mb-2 text-sm">
                                        {{ $t('components.risk_mitigation.title') }}
                                        <span class="text-red-500">{{ $t('general.asterisk') }}</span>
                                    </p>
                                    <Dropdown
                                        :placeholder="$t('components.risk_mitigation.title')"
                                        :options="modalData.riskMitigation.title.options"
                                        :value="modalData.riskMitigation.title.model"
                                        v-bind="singleSelectConfig"
                                        :disabled="isTitleDisabled"
                                        @input="(value) => setRiskMitigationField(value, 'title')"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="note-text-area gap-5 h-full flex flex-col flex-1 items-start w-full">
                            <quill-editor
                                v-model="modalData.note"
                                ref="myQuillEditor"
                                :options="editorOptions"
                            />
                        </div>
                    </FormulateForm>
                </div>
            </div>

            <footer class="flex items-end justify-between pb-4 px-6 bg-card-bg">
                <div class="flex items-center gap-6 -ml-6 -mb-4 px-8 py-4 bg-main rounded-tr-xl">

                    <label
                        class="flex items-center justify-center gap-2 relative cursor-pointer"
                        for="taskFile"
                    >
                        <EncloseIcon />
                        <span
                            v-if="modalData.files.length > 0"
                            class="absolute whitespace-nowrap top-0 right-0 h-4 w-4 inline-flex items-center justify-center px-1 py-0.5 text-2xs font-bold leading-none text-red-100 transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full"
                        >
                            {{ modalData.files.length }}
                        </span>
                        <input
                            id="taskFile"
                            type="file"
                            name="taskFile"
                            multiple="multiple"
                            :disabled="isLoading"
                            class="hidden"
                            @change="uploadFile($event)"
                        />
                    </label>

                    <button
                        class="flex items-center justify-center gap-2"
                        variant="alt"
                        :disabled="isLoading"
                        @click="resetFiles"
                    >
                        <DelIcon />
                    </button>
                </div>
                <div  class="ml-auto relative">
                    <Button
                        v-if="checkPermission('case.actions_internal')"
                        type="submit"
                        :text="$t('components.tenant.sc_journey.log_activity')"
                        class="transition btn-primary w-32 text-sm border-none font-semibold rounded-xl px-4 py-2"
                        :disabled="isSubmitDisabled"
                        @click="saveForm"
                    />
                </div>
            </footer>
        </div>
    </modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "@/axios";
import { quillEditor } from "vue-quill-editor";
import "quill/dist/quill.snow.css";
import { cloneDeep, uniqBy } from "lodash";
import Field from "@/components/sc-journey/field-modal.vue";
import Button from "@/components/button/index.vue";
import Dropdown from "@shared/components/dropdown-base";
import CloseIcon from "@/assets/icons/email-admin/icon-crossbt.svg";
import TaskIcon from "@/assets/icons/profile-view/wTask.svg";
import DateIcon from "@/assets/icons/dashboard/date.svg";
import TimeIcon from "@/assets/icons/dashboard/clock.svg";
import DelIcon from "@/assets/icons/svg/delete-icon-white.svg";
import EncloseIcon from "@/assets/icons/profile-view/enclosure-icon-white.svg";
import { checkPermission } from "@shared/utils/functions"
import { userType } from "@shared/utils/constants";
import {
    isFileSizeExceedsLimit,
    formatDate,
    mapUsersOptions,
} from "./utils.js";
import {
    MODAL_DATA,
    MULTI_SELECT_CONFIG,
    SINGLE_SELECT_CONFIG,
    FILE_COUNT_LIMIT,
} from "./constants.js";

export default {
    name: "profile-task-modal",

    props: {
        isOpened: {
            type: Boolean,
            required: true,
        },
        isClient: {
            type: Boolean,
            default: false  
        },
        activeTaskData: {
            type: Object,
            default: () => ({}),
        },
    },

    components: {
        quillEditor,
        Button,
        TaskIcon,
        Field,
        CloseIcon,
        DateIcon,
        TimeIcon,
        DelIcon,
        EncloseIcon,
        Dropdown,
    },

    data() {
        return {
            modalData: cloneDeep(MODAL_DATA),
            isLoading: false,
            assignee: null
        };
    },

    methods: {
        checkPermission,

        async fetchAssignees() {
            const { data: userData } = await axios.get(`case-tasks/${this.caseId}/assignees`);
            this.modalData.tenantUsers.options = userData.tenant_users;
            this.modalData.clientUsers.options = userData.client_users;
        },

        async fetchRiskMitigationData() {
            const { data: riskMitigationData } = await axios.get(`case-tasks/${this.caseId}/risk-mitigations`);
            this.modalData.riskMitigation.rawData = riskMitigationData?.data ?? [];
            this.modalData.riskMitigation.category.options = uniqBy(this.modalData.riskMitigation.rawData
                .map(el => ({ label: el?.category, value: el?.category })), 'label');
        },

        async saveForm() {
            this.modalData.note = this.modalData.note.replace(/<\/?[^>]+>/ig, "").trim();
            if (!this.modalData.note) {
                return this.$toast.error(this.$t('components.tenant.sc_journey.empty_note_are_not_allowed'));
            }

            const payload = this.preparePayload();

            try {
                const filePathIds = await this.uploadFiles();

                if (filePathIds.length > 0) {
                    payload.files = filePathIds;
                }

                await axios.post("case-tasks", payload);
                this.$toast.success(this.$t("components.tenant.sc_journey.task_created_successfully"));
                this.$emit("submit");
            } catch (error) {
                console.error("Error creating case task:", error);
                this.$toast.error(error.response.data.detail || this.$t("general.default_error"));
            } finally {
                this.closeModal();
                this.resetForm();
            }
        },

        async uploadFiles() {
            const filePromises = this.prepareFilePromises();
            let filePathIds = [];

            if (filePromises.length > 0) {
                const files = await Promise.all(filePromises);
                filePathIds = files?.map(el => el?.data?.data?.file_path).filter(Boolean);
            }

            return filePathIds;
        },

        preparePayload() {
            const { id, category, sub_category, title } = this.getRiskMitigationItem();

            const payload = {
                case_id: this.caseId,
                external_id: this.activeTaskData?.id || this.entityId || this.caseId,
                external_id_type: this.activeTaskData?.type
                    ? this.activeTaskData?.type
                        : this.entityId
                        ? "case_entity"
                    : "candidate",
                risk_mitigation: this.isRiskMitigationMode,
                risk_mitigation_id: id ?? null,
                risk_mitigation_category: category ?? null,
                risk_mitigation_sub_category: sub_category ?? null,
                risk_mitigation_title: title ?? null,
                task_text: this.modalData.note,
                case_entity_id: this.entityId,
                target_time: formatDate(this.modalData.dueDate, this.modalData.time),
                assignees: [
                    ...this.modalData.tenantUsers.model.map(el => ({ user_id: el.id, user_type: el.user_type })),
                    ...this.modalData.clientUsers.model.map(el => ({ user_id: el.id, user_type: el.user_type }))
                ],
                files: [],
            };

            return payload;
        },

        prepareFilePromises() {
            return this.modalData.files.map(file => {
                const formData = new FormData();
                formData.append("file", file);
                return axios.post(`case-tasks/${this.caseId}/entity/${this.entityId}/upload-file`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
            });
        },

        handleDateChange() {
            if (this.modalData.dueDate) {
                let dueDate = new Date(new Date(this.modalData.dueDate).setHours(0, 0, 0, 0)).getTime();
                let today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
                if (dueDate === today) {
                    this.modalData = { ...this.modalData, time: new Date(new Date().setHours(new Date().getHours() + 1, 0, 0)) };
                }else{
                    this.modalData = { ...this.modalData, time: new Date(new Date().setHours(0, 0, 0)) };
                }
            }
        },

        setRiskMitigationField(event, field) {
            this.modalData.riskMitigation[field].model = event;
            const { value } = event;

            switch(field) {
                case "category": {
                    this.modalData.riskMitigation.subCategory.model = null;
                    this.modalData.riskMitigation.subCategory.options = this.mapRiskMitigationOptions("category", "sub_category", value);
                    this.modalData.riskMitigation.title.model = null;
                    this.modalData.riskMitigation.title.options = [];
                    this.modalData.note = "";
                    break;
                }

                case "subCategory": {
                    this.modalData.riskMitigation.title.model = null;
                    this.modalData.riskMitigation.title.options = this.mapRiskMitigationOptions("sub_category", "title", value);
                    this.modalData.note = "";
                    break;
                }

                case "title": {
                    const item = this.modalData.riskMitigation.rawData.find(el => el.title === value) ?? {};
                    this.modalData.note = item.description ?? "";
                    break;
                }

                default:
                    break;
            }
        },

        mapRiskMitigationOptions(parentField, childField, value) {
            return uniqBy(this.modalData.riskMitigation.rawData
                .filter(el => el[parentField] === value)
                .map(el => ({ label: el?.[childField], value: el?.[childField] })), "value");
        },

        closeModal() {
            this.resetForm();
            this.$modal.hide("profile-task-modal");
        },

        resetForm() {
            this.modalData = cloneDeep(MODAL_DATA);
        },

        resetFiles() {
            this.modalData.files = [];
        },

        DisableDate(date) {
            return date < new Date(new Date().setHours(0, 0, 0, 0));
        },

        disabledHours(date) {
            let dueDate = new Date(new Date(this.modalData.dueDate).setHours(0, 0, 0, 0)).getTime();
            let today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
            if (dueDate === today) {
                return date.getHours() <= new Date().getHours();
            }
        },

        uploadFile(event) {
            const combinedFiles = [...this.modalData.files, ...event.target.files];
            if (combinedFiles.length > FILE_COUNT_LIMIT) {
                return this.$toast.error(this.$t("components.tenant.sc_journey.attached_files_exceeds_count", {
                    value: this.$t("components.tenant.sc_journey.task")
                }));
            }

            if (isFileSizeExceedsLimit(Array.from(event.target.files))) {
                return this.$toast.error(this.$t("components.tenant.sc_journey.attached_files_exceeds_size"));
            }

            const { files } = event.target;
            this.modalData.files.push(...Array.from(files));
        },

        toggleRiskMitigationMode() {
            this.modalData.riskMitigation.mode = !this.modalData.riskMitigation.mode;

            if (!this.isRiskMitigationMode) {
                this.modalData.riskMitigation.category.model = null;
                this.modalData.riskMitigation.subCategory.model = null;
                this.modalData.riskMitigation.title.model = null;

                this.modalData.note = "";
            }
        },

        getRiskMitigationItem() {
            const { value: category } = this.modalData.riskMitigation.category.model ?? {};
            const { value: subCategory } = this.modalData.riskMitigation.subCategory.model ?? {};
            const { value: title } = this.modalData.riskMitigation.title.model ?? {};
            const { rawData } = this.modalData.riskMitigation;

            const item = rawData.find(el => el.category === category && el.sub_category === subCategory && el.title === title) ?? {};
            return item;
        },
    },

    watch: {
        async isOpened(val) {
            if (val) {
                this.isLoading = true;
                this.$emit("onLoadStart");

                try {
                    await this.fetchAssignees();
                    await this.fetchRiskMitigationData();
                } catch (error) {
                    this.$toast.error(this.$t('general.error'));
                } finally {
                    this.isLoading = false;
                    this.$emit("onLoadEnd");
                }
            }
        }
    },

    computed: {
        ...mapGetters({
            tenantName: "getTenantName",
            getCaseData: "getCaseData",
            getUserType: "getUserType",
            getClientData: "getClientData",
        }),

        caseId() {
            return this.getCaseData?.id ?? null;
        },

        entityId() {
            return this.$route.query.entityId;
        },

        clientName() {
            return this.getCaseData?.client_name ?? {};
        },

        client(){
            return this.getUserType == userType.CLIENT;
        },

        tenantUsersOptions() {
            return mapUsersOptions(this.modalData.tenantUsers.options);
        },
        clientUsersOptions() {
            return mapUsersOptions(this.modalData.clientUsers.options);
        },

        isSubmitDisabled() {
            if (this.isRiskMitigationMode) {
                return this.baseSubmitValidation || this.usersValidation || this.riskMitigationValidation;
            }

            return this.baseSubmitValidation || this.usersValidation;
        },

        baseSubmitValidation() {
            return this.isLoading || !this.modalData.dueDate || !this.modalData.time || !this.modalData.note.trim();
        },

        usersValidation() {
            return !this.modalData.tenantUsers.model.length && !this.modalData.clientUsers.model.length;
        },

        riskMitigationValidation() {
            for (const field of ["category", "subCategory", "title"]) {
                if (!this.modalData.riskMitigation[field].model) {
                    return true;
                }
            }
            return false;
        },

        isSubCategoryDisabled() {
            return this.isSingleSelectDisabled || !this.modalData.riskMitigation.category.model;
        },

        isTitleDisabled() {
            return this.isSingleSelectDisabled || !this.modalData.riskMitigation.subCategory.model;
        },

        isSingleSelectDisabled() {
            return this.isLoading || !this.isRiskMitigationMode;
        },

        isRiskMitigationMode() {
            return this.modalData.riskMitigation.mode;
        },

        multiselectConfig() {
            return {
                ...MULTI_SELECT_CONFIG,
                loading: this.isLoading,
                disabled: this.isLoading,
            };
        },

        singleSelectConfig() {
            return {
                ...SINGLE_SELECT_CONFIG,
                loading: this.isLoading,
            };
        },

        editorOptions() {
            return {
                debug: "info",
                placeholder: this.$t('components.tenant.sc_journey.type_your_text_here'),
                theme: "snow",
                modules: {
                    toolbar: [[{ size: ["small", false, "large", "huge"] }], ["bold", "italic", "underline", "strike"]],
                },
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.modal-header {
    &__icon {
        fill: white;
        * {
            fill: white;
        }
    }
}
</style>
